'use client';

import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

export interface PlayerTrack {
  id: string;
  title: string;
  artist: string;
  coverUrl?: string;
  coverGradient?: string;
  audioUrl: string;
  duration?: string;
}

interface PlayerContextValue {
  currentTrack: PlayerTrack | null;
  isPlaying: boolean;
  progress: number; // 0-100
  volume: number; // 0-1
  duration: number; // seconds
  currentTime: number; // seconds
  playTrack: (track: PlayerTrack, snippet?: boolean) => void;
  togglePlay: () => void;
  seek: (pct: number) => void;
  setVolume: (v: number) => void;
  stopPlayer: () => void;
  isSnippet: boolean;
}

const PlayerContext = createContext<PlayerContextValue>({
  currentTrack: null,
  isPlaying: false,
  progress: 0,
  volume: 0.8,
  duration: 0,
  currentTime: 0,
  playTrack: () => {},
  togglePlay: () => {},
  seek: () => {},
  setVolume: () => {},
  stopPlayer: () => {},
  isSnippet: false,
});

export const usePlayer = () => useContext(PlayerContext);

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const [currentTrack, setCurrentTrack] = useState<PlayerTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isSnippet, setIsSnippet] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const snippetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSnippetTimer = () => {
    if (snippetTimerRef.current) {
      clearTimeout(snippetTimerRef.current);
      snippetTimerRef.current = null;
    }
  };

  const stopPlayer = useCallback(() => {
    clearSnippetTimer();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setCurrentTrack(null);
    setIsSnippet(false);
  }, []);

  const playTrack = useCallback((track: PlayerTrack, snippet = false) => {
    // Stop any existing playback
    clearSnippetTimer();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);

    setCurrentTrack(track);
    setIsSnippet(snippet);

    if (!track.audioUrl) {
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(track.audioUrl);
    audio.volume = volume;
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });

    audio.addEventListener('timeupdate', () => {
      if (!audioRef.current) return;
      const dur = audioRef.current.duration || 1;
      const cur = audioRef.current.currentTime;
      setCurrentTime(cur);
      setProgress((cur / dur) * 100);

      // Snippet: stop at 30s
      if (snippet && cur >= 30) {
        audioRef.current.pause();
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
      }
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    });

    audio.play().then(() => {
      setIsPlaying(true);
    }).catch(() => {
      setIsPlaying(false);
    });
  }, [volume]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying]);

  const seek = useCallback((pct: number) => {
    if (!audioRef.current) return;
    const dur = audioRef.current.duration || 0;
    audioRef.current.currentTime = (pct / 100) * dur;
    setProgress(pct);
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSnippetTimer();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  return (
    <PlayerContext.Provider value={{
      currentTrack,
      isPlaying,
      progress,
      volume,
      duration,
      currentTime,
      playTrack,
      togglePlay,
      seek,
      setVolume,
      stopPlayer,
      isSnippet,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}
